import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaChartLine,
  FaCode,
  FaHandshake,
  FaPalette,
  FaPlug,
  FaProjectDiagram,
  FaServer,
  FaShieldAlt,
  FaShoppingCart,
} from 'react-icons/fa';
import PageSeo from '../components/PageSeo';
import Newsletters from './Newsletters';
import {
  ABOUT_CLIENT_LOGOS,
  ABOUT_TESTIMONIALS,
  COMPANY_TIMELINE,
  DELIVERY_FLOW,
  HERO_CAPABILITY_NODES,
  OPERATING_PRINCIPLES,
  TEAM_CAPABILITIES,
  TEAM_POSITIONING,
  TRUST_METRICS,
} from '../data/aboutPremium';
import '../styles/about-premium.css';

const CAPABILITY_ICONS = {
  diagram: FaProjectDiagram,
  palette: FaPalette,
  code: FaCode,
  server: FaServer,
  cart: FaShoppingCart,
  plug: FaPlug,
  shield: FaShieldAlt,
};

function CapabilityIcon({ iconKey }: { iconKey: string }) {
  const Icon = CAPABILITY_ICONS[iconKey] || FaChartLine;
  return <Icon aria-hidden="true" />;
}

const About = () => {
  return (
    <>
      <PageSeo path="/about" />

      <div className="about-premium">
        <div className="header-spacer" />

        {/* Hero */}
        <section className="about-premium-hero" aria-labelledby="about-premium-heading">
          <div className="about-premium__shell">
            <div className="about-premium-hero__layout">
              <div>
                <p className="about-premium-hero__kicker">Engineering-first digital agency · Gautam Buddha Nagar, India</p>
                <h1 id="about-premium-heading" className="about-premium-hero__title">
                  About Plux Infosystems: senior web, ecommerce, AI, and integration engineering team
                </h1>
                <p className="about-premium-hero__lead">
                  We are an India-based web design, product engineering, and application development company
                  with a practical, engineering-first approach. Global clients rely on us for React and Next.js
                  products, Magento and WooCommerce, WordPress Sage CMS, AI automation, CRM/ERP integrations,
                  and long-term support—with clear communication and measurable outcomes.
                </p>
                <div className="about-premium-hero__actions">
                  <Link to="/getintouch" className="about-premium-btn about-premium-btn--primary">
                    Discuss your project
                  </Link>
                  <Link to="/services" className="about-premium-btn about-premium-btn--ghost">
                    Explore our services
                  </Link>
                </div>
              </div>

              <div className="about-premium-console" aria-hidden="true">
                {HERO_CAPABILITY_NODES.map((node) => (
                  <div key={node} className="about-premium-console__node">
                    {node}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Trust metrics */}
        <section className="about-premium-proof" aria-labelledby="about-proof-heading">
          <div className="about-premium__shell">
            <h2 id="about-proof-heading" className="sr-only">
              Why clients trust our delivery
            </h2>
            <div className="about-premium-proof__grid">
              {TRUST_METRICS.map((item) => (
                <article key={item.id} className="about-premium-proof__card">
                  <span className="about-premium-proof__value">{item.value}</span>
                  <span className="about-premium-proof__label">{item.label}</span>
                  <p className="about-premium-proof__detail">{item.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Company history */}
        <section className="about-premium-section" aria-labelledby="about-history-heading">
          <div className="about-premium__shell">
            <div className="about-premium-section__header">
              <p className="about-premium-section__kicker">Our story</p>
              <h2 id="about-history-heading" className="about-premium-section__title">
                From web design roots to full-stack engineering
              </h2>
              <p className="about-premium-section__intro">
                Plux Infosystems evolved from custom websites into a senior technical team delivering
                production-ready systems across ecommerce, CMS, integrations, AI, and support.
              </p>
            </div>
            <div className="about-premium-timeline">
              {COMPANY_TIMELINE.map((item) => (
                <article key={item.id} className="about-premium-timeline__item">
                  <span className="about-premium-timeline__phase">{item.phase}</span>
                  <h3 className="about-premium-timeline__title">{item.title}</h3>
                  <p className="about-premium-timeline__desc">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Technical team */}
        <section className="about-premium-section about-premium-section--dark" aria-labelledby="about-team-heading">
          <div className="about-premium__shell">
            <div className="about-premium-section__header">
              <p className="about-premium-section__kicker">Strong technical team</p>
              <h2 id="about-team-heading" className="about-premium-section__title">
                Senior engineers across design, product, integrations, and support
              </h2>
              <p className="about-premium-section__intro">
                Our team combines solution architecture, modern frontend and backend engineering,
                ecommerce expertise, and integration delivery—so clients get systems they can operate
                and improve long after launch.
              </p>
            </div>
            <p className="about-premium-team__statement">{TEAM_POSITIONING}</p>
            <div className="about-premium-capabilities">
              {TEAM_CAPABILITIES.map((cap) => (
                <article key={cap.id} className="about-premium-capability">
                  <span className="about-premium-capability__icon">
                    <CapabilityIcon iconKey={cap.iconKey} />
                  </span>
                  <h3 className="about-premium-capability__title">{cap.title}</h3>
                  <p className="about-premium-capability__desc">{cap.desc}</p>
                </article>
              ))}
            </div>
            <Link to="/leadership" className="about-premium-team__link">
              Meet our leadership <FaArrowRight aria-hidden="true" />
            </Link>
          </div>
        </section>

        {/* Delivery flow */}
        <section className="about-premium-section" aria-labelledby="about-flow-heading">
          <div className="about-premium__shell">
            <div className="about-premium-section__header">
              <p className="about-premium-section__kicker">How we work</p>
              <h2 id="about-flow-heading" className="about-premium-section__title">
                Directional delivery flow from understanding to improvement
              </h2>
              <p className="about-premium-section__intro">
                Every project follows a clear path—no mystery handoffs. We document, test, integrate,
                and support so delivery stays predictable and reliable.
              </p>
            </div>
            <div className="about-premium-flow">
              {DELIVERY_FLOW.map((step) => (
                <article key={step.id} className="about-premium-flow__step">
                  <span className="about-premium-flow__num">{step.step}</span>
                  <h3 className="about-premium-flow__name">{step.title}</h3>
                  <p className="about-premium-flow__desc">{step.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Operating principles */}
        <section className="about-premium-section" aria-labelledby="about-principles-heading">
          <div className="about-premium__shell">
            <div className="about-premium-section__header">
              <p className="about-premium-section__kicker">Operating principles</p>
              <h2 id="about-principles-heading" className="about-premium-section__title">
                Reliability and expertise you can verify in delivery
              </h2>
              <p className="about-premium-section__intro">
                We document, test, monitor, improve, and support—committed wording backed by how we
                actually run projects.
              </p>
            </div>
            <div className="about-premium-principles">
              {OPERATING_PRINCIPLES.map((item) => (
                <article key={item.id} className="about-premium-principle">
                  <h3>
                    <FaHandshake aria-hidden="true" /> {item.title}
                  </h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="about-premium-section about-premium-section--dark" aria-labelledby="about-testimonials-heading">
          <div className="about-premium__shell">
            <div className="about-premium-section__header">
              <p className="about-premium-section__kicker">Client proof</p>
              <h2 id="about-testimonials-heading" className="about-premium-section__title">
                What clients say about working with us
              </h2>
            </div>
            <div className="about-premium-testimonials">
              {ABOUT_TESTIMONIALS.map((item) => (
                <figure key={item.id} className="about-premium-testimonial">
                  <blockquote>
                    <p>&ldquo;{item.quote}&rdquo;</p>
                  </blockquote>
                  <figcaption className="about-premium-testimonial__author">
                    <span className="about-premium-testimonial__avatar" aria-hidden="true">
                      {item.initials}
                    </span>
                    <span>
                      <cite>{item.name}</cite>
                      <span className="about-premium-testimonial__company">{item.company}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* Client logos */}
        <section className="about-premium-section" aria-labelledby="about-clients-heading">
          <div className="about-premium__shell">
            <div className="about-premium-section__header">
              <p className="about-premium-section__kicker">Trusted partners</p>
              <h2 id="about-clients-heading" className="about-premium-section__title">
                Teams that have trusted us with production delivery
              </h2>
              <p className="about-premium-section__intro">
                Teams have trusted us with websites, ecommerce workflows, product interfaces,
                automation, and ongoing technical support.
              </p>
            </div>
            <div className="about-premium-clients__grid">
              {ABOUT_CLIENT_LOGOS.map((client) => (
                <div key={client.href} className="about-premium-clients__item">
                  <a href={client.href} target="_blank" rel="noreferrer">
                    <img
                      src={client.src}
                      alt={client.alt}
                      className={client.className || 'hover'}
                      loading="lazy"
                    />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="about-premium-cta" aria-labelledby="about-cta-heading">
          <div className="about-premium__shell">
            <div className="about-premium-cta__panel">
              <div className="about-premium-cta__copy">
                <h2 id="about-cta-heading">
                  Need a technical team that can own the full path from idea to support?
                </h2>
                <p>
                  We can help with architecture, delivery, integrations, optimization, and long-term
                  support—starting with a clear technical conversation.
                </p>
              </div>
              <div className="about-premium-cta__actions">
                <Link to="/getintouch" className="about-premium-btn about-premium-btn--primary">
                  Talk to our technical team <FaArrowRight aria-hidden="true" />
                </Link>
                <Link to="/services" className="about-premium-btn about-premium-btn--secondary">
                  View services
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Newsletters />
      </div>
    </>
  );
};

export default About;
